import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { useCart } from '@/providers/CartProvider'
import { useProducts } from '@/hooks/useProducts'
import { useBranding } from '@/branding/BrandingProvider'
import type { Product } from '@/types'

export function CartPage() {
  const { items, updateItem, removeItem, clearCart } = useCart()
  const { products } = useProducts()
  const { siteName } = useBranding()

  const productMap = new Map(products.map((p) => [p.id, p]))

  const cartProducts = items
    .map((item) => {
      const product = productMap.get(item.productId)
      return product ? { ...item, product } : null
    })
    .filter(Boolean) as { productId: string; quantity: number; product: Product }[]

  const subtotal = cartProducts.reduce((sum, item) => sum + item.product.price * item.quantity, 0)

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 h-16 flex items-center px-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-navy flex items-center justify-center">
            <Icon icon="solar:health-linear" className="text-white text-sm" />
          </div>
          <span className="text-lg font-medium text-navy tracking-tight">{siteName}</span>
        </Link>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-2xl font-medium tracking-tight text-navy mb-8">Your Cart</h1>

        {cartProducts.length === 0 ? (
          <div className="text-center py-16">
            <Icon icon="solar:cart-large-minimalistic-linear" className="text-5xl text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-light mb-6">Your cart is empty.</p>
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 bg-navy text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-8">
              {cartProducts.map((item) => (
                <div
                  key={item.productId}
                  className="bg-white border border-slate-200 rounded-xl p-6 flex items-center gap-6"
                >
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-navy">{item.product.name}</h3>
                    <p className="text-sm text-slate-500 font-light">
                      ${item.product.price}
                      {item.product.type === 'recurring' ? '/mo' : ' one-time'}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updateItem(item.productId, Math.max(1, item.quantity - 1))}
                      className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50"
                    >
                      -
                    </button>
                    <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                    <button
                      onClick={() => updateItem(item.productId, item.quantity + 1)}
                      className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50"
                    >
                      +
                    </button>
                  </div>

                  <div className="text-right min-w-[80px]">
                    <div className="font-medium text-navy">${item.product.price * item.quantity}</div>
                  </div>

                  <button
                    onClick={() => removeItem(item.productId)}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                  >
                    <Icon icon="solar:trash-bin-minimalistic-linear" className="text-xl" />
                  </button>
                </div>
              ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <span className="text-slate-600 font-light">Subtotal</span>
                <span className="text-2xl font-medium text-navy">${subtotal}</span>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={clearCart}
                  className="flex-1 py-3 rounded-full border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Clear Cart
                </button>
                <Link
                  to="/checkout"
                  className="flex-1 py-3 rounded-full bg-rust text-white text-sm font-medium text-center hover:bg-rust-dark transition-colors shadow-lg shadow-rust/20"
                >
                  Proceed to Checkout
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
